import moment from 'moment';
import _compact from 'lodash/compact';
import _get from 'lodash/get';

const defaultMomentFormat = {
  monthFormat: 'MMM-YYYY',
  dayFormat: 'M/D/YYYY',
  minuteFormat: 'M/D/YY - hh:mm A',
};

export type DateFormatOverrides = {
  monthFormat?: string;
  dayFormat?: string;
  minuteFormat?: string;
};

export const dateToString = (
  date: string,
  formatOverride: DateFormatOverrides = {},
  isUtc: boolean = false,
) => {
  if (!date || isNaN(Date.parse(date))) return 'N/A';

  const re = /^\d{4}(?:-(\d{2})(?:-(\d{2})(?:T(\d{2}:\d{2})(:\d{2})?)?)?)?/;
  const match = date.match(re);
  if (!match) return 'N/A';

  let format;
  switch (_compact(match).length) {
    case 1:
      // moment doesn't like simple year values, bypass formatting
      return date;
    case 2:
      format = formatOverride.monthFormat || defaultMomentFormat.monthFormat;
      break;
    case 3:
      format = formatOverride.dayFormat || defaultMomentFormat.dayFormat;
      break;
    default:
      format = formatOverride.minuteFormat || defaultMomentFormat.minuteFormat;
      break;
  }

  return isUtc ? moment.utc(date).format(format) : moment(date).format(format);
};

export const valueUnitToString = (value: number | string, unit: string) => {
  if (value == null) {
    return null;
  } else if (!unit) {
    return String(value);
  } else {
    return `${value} ${unit}`;
  }
};

export type ObjectWithSystem = { system?: string } & any;
const getSystemCheck =
  (system: string | string[]) =>
  (checkValue: ObjectWithSystem = {}) => {
    // no target system specified, return true
    if (!system || (Array.isArray(system) && !system.length)) return true;

    if (Array.isArray(system)) {
      // atleast one target system matches checkValue system
      return system.some(system => system === checkValue.system);
    } else {
      return system === checkValue.system;
    }
  };

export type ObjectWithCode = { code?: string } & any;
const getCodeCheck =
  (code: string | string[]) =>
  (checkValue: ObjectWithCode = {}) => {
    // no target code specified, return true
    if (!code || (Array.isArray(code) && !code.length)) return true;

    if (Array.isArray(code)) {
      // atleast one target code matches coding code
      return code.some(code => code === checkValue.code);
    } else {
      return code === checkValue.code;
    }
  };

export type ObjectWithUrl = { url?: string } & any;
const getUrlCheck =
  (url: string | string[]) =>
  (checkValue: ObjectWithUrl = {}) => {
    // no target url specified, return true
    if (!url || (Array.isArray(url) && !url.length)) return true;

    if (Array.isArray(url)) {
      // atleast one target code matches coding code
      return url.some(url => url === checkValue.url);
    } else {
      return url === checkValue.url;
    }
  };

// https://www.hl7.org/fhir/datatypes.html#coding
export const filterCodings = (
  codings: fhir.Coding[] = [],
  codingSystem: string | string[],
  codingCode: string | string[],
) => {
  if (codings.length === 0) return null;

  const systemCheck = getSystemCheck(codingSystem);
  const codeCheck = getCodeCheck(codingCode);

  return (
    codings.find(coding => codeCheck(coding) && systemCheck(coding)) || null
  );
};

const getCodingsCheck =
  (codingSystem: string | string[], codingCode: string | string[]) =>
  (codings: fhir.Coding[]) =>
    !!filterCodings(codings, codingSystem, codingCode);

// https://www.hl7.org/fhir/extensibility.html
export const filterExtensions = (
  extensions: fhir.Extension[] = [],
  extensionUrl: string | string[],
) => {
  if (extensions.length === 0) return null;

  const urlCheck = getUrlCheck(extensionUrl);
  return extensions.find(urlCheck) || null;
};

// https://www.hl7.org/fhir/datatypes.html#Identifier
export const filterIdentifiers = (
  identifiers: fhir.Identifier[] = [],
  identifierSystem: string | string[],
  typeSystem: string | string[],
  typeCode: string | string[],
  extensionUrl: string | undefined,
) => {
  if (identifiers.length === 0) return null;

  const identifierSystemCheck = getSystemCheck(identifierSystem);
  const identifersOfSystem = identifiers.filter(identifier =>
    identifierSystemCheck(identifier),
  );

  if (identifersOfSystem.length === 0) return null;

  const codingsCheck = getCodingsCheck(typeSystem, typeCode);
  const identifiersOfSystemAndTypeCoding = identifersOfSystem.filter(
    identifier => {
      const identifierType = _get<
        fhir.Identifier,
        'type',
        fhir.CodeableConcept
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      >(identifier, 'type', null);
      const typeCodings = _get<fhir.CodeableConcept, 'coding', fhir.Coding[]>(
        identifierType,
        'coding',
        [],
      );
      return codingsCheck(typeCodings);
    },
  );
  // Find identifier with a matching extension.url, if provided:
  const targetIdentifiers = identifiersOfSystemAndTypeCoding.filter(
    identifier =>
      !extensionUrl || !!filterExtensions(identifier.extension, extensionUrl),
  );

  // Prefer the identifier with the extension. If not, just return what we have:
  if (targetIdentifiers.length) {
    return targetIdentifiers;
  }

  if (!identifiersOfSystemAndTypeCoding.length) {
    return null;
  }
  return identifiersOfSystemAndTypeCoding;
};
